/**
 * The intent finder's clarifying question, from the client's side (ACOP-147).
 *
 * AVS OS asks "Which product line?" (or "today, or planned?") as text AND, now, as `options`. A person
 * may answer by pressing a button or by TYPING — "2", "Copilot", "copilot." — and either way the answer
 * must go back WITH the question it answers. It used to go back as the question itself, so a typed
 * "2" was searched for as the text "2" (stgn, 2026-10-13).
 *
 * Free of React so every rule is unit-testable — CopilotTwo imports the router, which this Jest
 * cannot resolve.
 */

export type ClarifyOption = { label: string; value: string };

export type ClarifyData = {
  clarifyStep?: string;
  clarifyingQuestion?: string;
  /** What AVS OS offers, in the order its text numbers them. */
  options?: ClarifyOption[];
  /** Legacy shape: bare product names, from an AVS OS that predates `options`. */
  detectedSegments?: string[];
};

/** What to show as buttons. Prefers `options`; falls back to the legacy bare-segment list. */
export function clarifyOptions(data: ClarifyData | null | undefined): ClarifyOption[] {
  if (!data) return [];
  if (Array.isArray(data.options) && data.options.length > 0) {
    return data.options.filter((o) => o && typeof o.label === "string" && typeof o.value === "string");
  }
  if (Array.isArray(data.detectedSegments) && data.detectedSegments.length > 0) {
    return data.detectedSegments.map((seg) => ({ label: seg, value: seg }));
  }
  return [];
}

/** Lower-case, with punctuation and surrounding words like "option" / "number" stripped. */
function normalise(text: string): string {
  return (text || "")
    .toLowerCase()
    .replace(/["'`.!?,;:()*]/g, " ")
    .replace(/\b(option|number|choice|the|product|line|please|pick|choose)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Is this typed text an answer to the question just asked? A number (1..n, in the listed order), or
 * an option's name. Anything else is NOT an answer — it is a new question, and the caller treats it
 * as one rather than swallowing it.
 */
export function resolveClarifyReply(text: string, options: ClarifyOption[]): ClarifyOption | null {
  const clean = normalise(text);
  if (!clean || options.length === 0) return null;

  if (/^\d{1,2}$/.test(clean)) {
    const index = Number(clean) - 1;
    return index >= 0 && index < options.length ? options[index] : null;
  }
  return (
    options.find((o) => normalise(o.label) === clean || normalise(o.value) === clean) ?? null
  );
}

/**
 * What to send for a chosen option. A `segment` step is answered with the product line itself (the
 * route pins it); any other step (today-or-planned) is answered with the option's reply token. In
 * both cases the ORIGINAL question travels as the question.
 */
export function requestForPick(
  clarifyStep: string | undefined,
  option: ClarifyOption
): { segment: string | null; clarifyStep: string; clarifyReply: string | null } {
  const step = clarifyStep || "segment";
  return step === "segment"
    ? { segment: option.value, clarifyStep: step, clarifyReply: null }
    : { segment: null, clarifyStep: step, clarifyReply: option.value };
}

export type SubmitPlan =
  | { kind: "pick"; question: string; option: ClarifyOption; step: string }
  | { kind: "fresh" };

/**
 * What should typing `text` do right now? If a clarifying question is waiting AND the text answers
 * it, it is a pick (resending the pending question). Otherwise it is a fresh question — including
 * when the text is something else entirely, so a person is never trapped by a question they ignore.
 */
export function planSubmit(
  text: string,
  clarify: ClarifyData | null | undefined,
  pendingQuestion: string | null | undefined
): SubmitPlan {
  if (!clarify || !pendingQuestion) return { kind: "fresh" };
  const option = resolveClarifyReply(text, clarifyOptions(clarify));
  if (!option) return { kind: "fresh" };
  return { kind: "pick", question: pendingQuestion, option, step: clarify.clarifyStep || "segment" };
}
